import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Clock, Download, Linkedin, Loader2, Mail, MapPin } from "lucide-react";
import { SectionLabel } from "@/components/common";
import { api, errMsg, useFetch } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSeo } from "@/lib/seo";
import { useSite, openResume } from "@/lib/site";
import { cn } from "@/lib/utils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const inputCls = "w-full rounded-md border bg-transparent px-4 py-3.5 text-base text-fg placeholder:text-silver/60 transition-colors focus:border-turq focus:outline-none";

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className="label-mono mb-2.5 block">{label}{required && <span className="text-turq"> *</span>}</label>
    {children}
    {error && <p id={`${id}-error`} className="mt-2 text-sm text-[rgb(var(--err))]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const Sidebar = ({ settings }) => (
  <aside className="space-y-8 lg:sticky lg:top-28">
    <ul className="space-y-5 text-sm">
      <li><a href={`mailto:${settings.contact_email}`} data-testid="contact-email-link" className="group flex items-center gap-3 break-all hover:text-turq"><Mail className="h-4 w-4 shrink-0 text-gold" aria-hidden />{settings.contact_email}</a></li>
      <li><a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-3 hover:text-turq"><Linkedin className="h-4 w-4 text-gold" aria-hidden />LinkedIn profile</a></li>
      <li className="flex items-center gap-3 text-silver"><MapPin className="h-4 w-4 text-gold" aria-hidden />{settings.location}</li>
      <li className="flex items-center gap-3 text-silver"><Clock className="h-4 w-4 text-gold" aria-hidden />Replies within two business days</li>
    </ul>
    <button type="button" onClick={() => openResume(settings)} data-testid="contact-download-resume" className="inline-flex items-center gap-2 rounded-full border border-gold/60 px-6 py-3 text-sm text-gold transition-colors hover:bg-gold hover:text-canvas">
      <Download className="h-4 w-4" aria-hidden /> Download Résumé
    </button>
  </aside>
);

const matchType = (types, raw) => {
  const v = (raw || "").toLowerCase();
  return types.find((t) => t.slug === v || t.label.toLowerCase() === v)?.slug || "";
};

export default function Contact() {
  const { settings } = useSite();
  const { data: buildOptions } = useFetch("/build-options");
  const [params] = useSearchParams();
  const types = settings?.inquiry_types || [];
  const projectTypes = useMemo(() => (buildOptions || []).map((b) => b.label), [buildOptions]);
  const initialProject = (buildOptions || []).find((b) => b.key === params.get("project"))?.label || "";
  const [form, setForm] = useState(() => ({ inquiry: matchType(types, params.get("type")), name: "", company: "", email: "", project: "", budget: "", timeline: "", description: "", website: "" }));
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  useSeo({ title: settings ? `Start a project — ${settings.name}` : undefined, description: "Projects, roles and collaborations — tell me what you're building." });
  const formRef = useRef(null);
  const projectValue = form.project || initialProject;
  const typeParam = params.get("type");
  useEffect(() => {
    const t = matchType(types, typeParam);
    if (t) setForm((f) => ({ ...f, inquiry: t }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeParam]);

  if (!settings) return null;
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
    if (state === "error") setState("idle");
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.inquiry) er.inquiry = "Choose what this inquiry is about.";
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (form.description.trim().length < 20) er.description = "Tell me a little more — at least 20 characters.";
    setErrors(er);
    const first = Object.keys(er)[0];
    if (first) {
      formRef.current?.querySelector(first === "inquiry" ? "[data-inquiry-first]" : `#${first}`)?.focus();
      return;
    }
    setState("sending");
    try {
      await api.post("/inquiries", {
        inquiry_type: form.inquiry, name: form.name, company: form.company, email: form.email, project_type: projectValue,
        budget: form.budget, timeline: form.timeline, description: form.description, website: form.website,
      });
      track("generate_lead", { inquiry_type: form.inquiry, project_type: projectValue });
      setState("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const s = e.response?.status;
      setServerError(s === 429 || s === 422 ? errMsg(e) : "Your message couldn't be sent right now. Please try again in a moment, or email me directly.");
      setState("error");
    }
  };

  const reset = () => {
    setForm((f) => ({ ...f, name: "", company: "", email: "", description: "", budget: "", timeline: "" }));
    setState("idle");
  };

  return (
    <div data-testid="contact-page" className="pt-[72px]">
      <section className="container-x grid gap-16 py-16 md:py-24 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionLabel number="—">Contact</SectionLabel>
          <h1 className="mb-10 mt-6 font-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">Start a <em className="text-turq">project</em>.</h1>
          <p className="mb-10 max-w-sm text-base leading-relaxed text-silver">Projects, roles, collaborations or an idea that doesn't fit a category yet — tell me what you're working on.</p>
          <Sidebar settings={settings} />
        </div>

        {state === "success" ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} role="status" data-testid="contact-success-state" className="flex flex-col justify-center rounded-lg border border-turq/40 bg-turq/5 p-8 md:p-14 lg:col-span-7 lg:col-start-6">
            <CheckCircle2 className="h-10 w-10 text-turq" aria-hidden />
            <p className="label-mono mt-8"><span className="text-gold">—</span> Inquiry received</p>
            <h2 className="mt-4 font-display text-4xl leading-tight md:text-6xl">Thank you, {form.name.split(" ")[0]}.</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-silver md:text-lg">Your message is in my inbox. I read every inquiry personally and reply within two business days — usually sooner — at <span className="text-fg">{form.email}</span>.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button type="button" onClick={reset} data-testid="contact-send-another" className="rounded-full border border-line/25 px-6 py-3 text-sm hover:border-turq hover:text-turq">Send another inquiry</button>
              <a href="/work" className="rounded-full bg-turq px-6 py-3 text-sm text-onturq hover:bg-fg hover:text-canvas">Browse the work</a>
            </div>
          </motion.div>
        ) : (
        <form ref={formRef} onSubmit={submit} noValidate className="space-y-10 lg:col-span-7 lg:col-start-6" data-testid="contact-form" aria-describedby="contact-form-note">
          <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="contact-honeypot" />
          </div>
          <fieldset>
            <legend className="label-mono mb-4">What is this about?<span className="text-turq"> *</span></legend>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-invalid={!!errors.inquiry}>
              {types.map((t, i) => {
                const on = form.inquiry === t.slug;
                return (
                  <button
                    key={t.slug}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-inquiry-first={i === 0 ? true : undefined}
                    onClick={() => set("inquiry")({ target: { value: t.slug } })}
                    data-testid={`inquiry-type-${t.slug}`}
                    className={cn("rounded-full border px-4 py-2.5 text-sm transition-[background-color,color,border-color] duration-300", on ? "border-turq bg-turq text-onturq" : "border-line/20 text-fg hover:border-turq")}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {errors.inquiry && <p className="mt-3 text-sm text-[rgb(var(--err))]" data-testid="inquiry-error">{errors.inquiry}</p>}
          </fieldset>

          <div className="grid gap-8 md:grid-cols-2">
            <Field id="name" label="Name" required error={errors.name}>
              <input id="name" value={form.name} onChange={set("name")} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} data-testid="contact-name-input" className={cn(inputCls, errors.name ? "border-[rgb(var(--err))]" : "border-line/20")} />
            </Field>
            <Field id="company" label="Company">
              <input id="company" value={form.company} onChange={set("company")} autoComplete="organization" data-testid="contact-company-input" className={cn(inputCls, "border-line/20")} />
            </Field>
            <Field id="email" label="Email" required error={errors.email}>
              <input id="email" type="email" value={form.email} onChange={set("email")} autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} data-testid="contact-email-input" className={cn(inputCls, errors.email ? "border-[rgb(var(--err))]" : "border-line/20")} />
            </Field>
            <Field id="project" label="Project Type">
              <select id="project" value={projectValue} onChange={set("project")} data-testid="contact-project-type-select" className={cn(inputCls, "border-line/20 bg-canvas")}>
                <option value="">Select…</option>
                {projectTypes.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field id="budget" label="Budget Range">
              <select id="budget" value={form.budget} onChange={set("budget")} data-testid="contact-budget-select" className={cn(inputCls, "border-line/20 bg-canvas")}>
                <option value="">Select…</option>
                {settings.budget_ranges.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field id="timeline" label="Timeline">
              <select id="timeline" value={form.timeline} onChange={set("timeline")} data-testid="contact-timeline-select" className={cn(inputCls, "border-line/20 bg-canvas")}>
                <option value="">Select…</option>
                {settings.timelines.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field id="description" label="Project Description" required error={errors.description}>
            <textarea id="description" rows={6} value={form.description} onChange={set("description")} aria-invalid={!!errors.description} aria-describedby={errors.description ? "description-error" : undefined} data-testid="contact-description-input" placeholder="What are you trying to build, change or solve?" className={cn(inputCls, "resize-y", errors.description ? "border-[rgb(var(--err))]" : "border-line/20")} />
          </Field>

          <div className="flex flex-wrap items-center gap-6">
            <button type="submit" disabled={state === "sending"} data-testid="contact-submit-button" className="inline-flex items-center gap-2 rounded-full bg-turq px-8 py-4 text-sm font-medium text-onturq transition-colors duration-300 hover:bg-fg hover:text-canvas disabled:opacity-60">{state === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}{state === "sending" ? "Sending…" : "Send inquiry"}</button>
            <p id="contact-form-note" className="text-xs text-silver">Fields marked * are required.</p>
          </div>

          <AnimatePresence>
            {state === "error" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} role="alert" data-testid="contact-error-state" className="flex items-start gap-4 rounded-md border border-[rgb(var(--err))]/50 bg-[rgb(var(--err))]/5 p-6">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--err))]" aria-hidden />
                <div>
                  <p className="font-medium">Message not sent.</p>
                  <p className="mt-1 text-sm text-silver">{serverError} <a href={`mailto:${settings.contact_email}`} className="text-turq underline underline-offset-4">{settings.contact_email}</a></p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
        )}
      </section>
    </div>
  );
}
